/**
 * UserModern - MIGRATED to use CRUDPage template
 * 
 * Before: 354 lines
 * After: ~120 lines (66% reduction!)
 * 
 * Features:
 * - User CRUD with deactivation support
 * - Deactivated users cannot access the org but data is preserved
 * - Admins can reactivate users
 */

import React, { useState, useEffect } from 'react';
import { Box, Avatar, Chip, Button, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Switch, FormControlLabel, Tooltip } from '@mui/material';
import { Users, Mail, Phone, UserPlus, MoreVertical, Edit, Trash2, UserX, UserCheck } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';

import CRUDPage from '../Components/UI/CRUDPage';
import InviteUserModal from '../Components/Users/InviteUserModal';
import { usePermissions } from '../contexts/PermissionContext';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';

// Column definitions - function to generate columns with is_active support
const getColumns = (showInactive) => [
  {
    field: 'avatar',
    headerName: 'Avatar',
    sortable: false,
    renderCell: ({ row }) => (
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: row.is_active === false ? 'grey.400' : 'primary.main',
          fontSize: '0.875rem',
          opacity: row.is_active === false ? 0.6 : 1
        }}
      >
        {row.firstname?.charAt(0)}{row.lastname?.charAt(0)}
      </Avatar>
    )
  },
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,
    renderCell: ({ row }) => (
      <Box sx={{ opacity: row.is_active === false ? 0.6 : 1 }}>
        <Box sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.firstname} {row.lastname}
          {row.is_active === false && (
            <Chip 
              label="Inactive" 
              size="small" 
              color="default"
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          )}
        </Box>
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {row.role_name || 'No role'}
        </Box>
      </Box>
    )
  },
  {
    field: 'email',
    headerName: 'Email',
    sortable: true,
    renderCell: ({ row, value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', opacity: row.is_active === false ? 0.6 : 1 }}>
        <Mail size={14} />
        <span>{value}</span>
      </Box>
    )
  },
  {
    field: 'phonenumber',
    headerName: 'Phone',
    sortable: false,
    renderCell: ({ row, value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', opacity: row.is_active === false ? 0.6 : 1 }}>
        <Phone size={14} />
        <span>{value || '-'}</span>
      </Box>
    )
  },
  {
    field: 'department',
    headerName: 'Department',
    sortable: true,
    renderCell: ({ row, value }) => (
      <Chip
        label={value || 'No department'}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.75rem', opacity: row.is_active === false ? 0.6 : 1 }}
      />
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    align: 'right'
  }
];

// Transform data for form submission (edit only)
const transformForSubmit = (formData) => {
  const data = { ...formData };
  // Remove password field if it's empty
  if (!data.password) {
    delete data.password;
  }
  return data;
};

function UserModern() {
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [actionAnchor, setActionAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const { can } = usePermissions();
  const { selectedOrgId, loading: orgLoading } = useOrg();
  const canCreate = can('Admin', 'users', 'create');
  const canDelete = can('Admin', 'users', 'delete');

  // Handle action menu
  const handleActionClick = (event, user) => {
    setActionAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionClose = () => {
    setActionAnchor(null);
    setSelectedUser(null);
  };

  // Deactivate user
  const handleDeactivate = async (user) => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      await axios.patch(
        `${BASE_URL}/api/users/${user.id}/deactivate?organisation_id=${selectedOrgId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${user.firstname} ${user.lastname} has been deactivated`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deactivating user:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to deactivate user';
      toast.error(errorMessage);
    }
    handleActionClose();
  };

  // Activate user
  const handleActivate = async (user) => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      await axios.patch(
        `${BASE_URL}/api/users/${user.id}/activate?organisation_id=${selectedOrgId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${user.firstname} ${user.lastname} has been reactivated`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error activating user:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to activate user';
      toast.error(errorMessage);
    }
    handleActionClose();
  };

  // Custom action menu renderer
  const renderUserActions = ({ row, onEdit, onDelete, canEdit, canDelete }) => (
    <>
      <IconButton 
        size="small" 
        onClick={(e) => handleActionClick(e, row)}
      >
        <MoreVertical size={18} />
      </IconButton>
      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor) && selectedUser?.id === row.id}
        onClose={handleActionClose}
      >
        {canEdit && (
          <MenuItem onClick={() => { handleActionClose(); onEdit?.(); }}>
            <ListItemIcon><Edit size={16} /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canDelete && row.is_active !== false && (
          <MenuItem onClick={() => handleDeactivate(row)}>
            <ListItemIcon><UserX size={16} /></ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
        )}
        {canDelete && row.is_active === false && (
          <MenuItem onClick={() => handleActivate(row)}>
            <ListItemIcon><UserCheck size={16} /></ListItemIcon>
            <ListItemText>Reactivate</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => { handleActionClose(); onDelete?.(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><Trash2 size={16} color="inherit" /></ListItemIcon>
            <ListItemText>Delete Permanently</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );

  // Fetch departments and roles for dropdowns
  useEffect(() => {
    const fetchOptions = async () => {
      // Double-check selectedOrgId is valid before fetching
      if (!selectedOrgId || selectedOrgId === '') {
        console.warn('Cannot fetch options: No organization selected');
        return;
      }

      try {
        const user = getAuth().currentUser;
        if (!user) {
          console.warn('Cannot fetch options: No authenticated user');
          return;
        }
        const token = await user.getIdToken();

        // Fetch departments for this organization
        const deptResponse = await axios.get(`${BASE_URL}/api/options/departments?organisation_id=${selectedOrgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDepartments(deptResponse.data.map(d => ({
          value: d.id,
          label: d.name
        })));

        // Fetch roles
        const rolesResponse = await axios.get(`${BASE_URL}/api/roles-options?organisation_id=${selectedOrgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRoles(rolesResponse.data.map(r => ({
          value: r.role_code,
          label: r.role_name
        })));
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
      }
    };

    // Wait for org to be loaded and valid before fetching
    if (!orgLoading && selectedOrgId) {
      fetchOptions();
    }
  }, [selectedOrgId, orgLoading]);

  const handleInviteSuccess = () => {
    // Trigger refresh of user list
    setRefreshTrigger(prev => prev + 1);
  };

  // Field definitions with dynamic options
  const userFields = [
    {
      name: 'firstname',
      label: 'First Name',
      type: 'text',
      required: true,
      validation: {
        required: 'First name is required',
        minLength: { value: 1, message: 'First name is required' }
      }
    },
    {
      name: 'lastname',
      label: 'Last Name',
      type: 'text',
      required: true,
      validation: {
        required: 'Last name is required',
        minLength: { value: 1, message: 'Last name is required' }
      }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      validation: {
        required: 'Email is required',
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address'
        }
      }
    },
    {
      name: 'phonenumber',
      label: 'Phone Number',
      type: 'text',
      required: true,
      validation: {
        required: 'Phone number is required',
        pattern: {
          value: /^[0-9+\-\s()]+$/,
          message: 'Invalid phone number format'
        }
      }
    },
    {
      name: 'department_id',
      label: 'Department',
      type: 'select',
      options: departments
    },
    {
      name: 'role_code',
      label: 'Role',
      type: 'select',
      options: roles
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      helperText: 'Leave blank to keep existing password (edit mode)',
      validation: {
        minLength: { value: 6, message: 'Password must be at least 6 characters' }
      }
    }
  ];

  return (
    <>
      <CRUDPage
        key={`${refreshTrigger}-${showInactive}`}
        title="User Management"
        subtitle="Manage system users and access"
        icon={Users}
        columns={getColumns(showInactive)}
        fields={userFields}
        searchFields={['firstname', 'lastname', 'email', 'phonenumber', 'department', 'role_name']}
        permissions={{
          resource: 'Admin',
          module: 'users'
        }}
        crudConfig={{
          endpoint: `/api/users${showInactive ? '?include_inactive=true' : ''}`,
          entityName: 'User',
          entityNamePlural: 'Users',
          useOrgId: true,
          disableCreate: true,
          transformUpdate: (data) => transformForSubmit(data, true),
          deleteWarning: 'This will permanently delete the user and all their data. Consider deactivating instead.'
        }}
        renderCustomActions={renderUserActions}
        headerActions={
          <>
            <Tooltip title="Show users who have been deactivated">
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    size="small"
                  />
                }
                label="Show Inactive"
                sx={{ mr: 2 }}
              />
            </Tooltip>
            {canCreate && (
              <Button
                variant="outlined"
                startIcon={<UserPlus size={18} />}
                onClick={() => setInviteModalOpen(true)}
                sx={{ mr: 1 }}
              >
                Invite User
              </Button>
            )}
          </>
        }
      />

      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        organisationId={selectedOrgId}
        onSuccess={handleInviteSuccess}
      />
    </>
  );
}

export default UserModern;
